/** The four things people do every day, under the thumb.
 *
 *  On a phone the rail is an off-canvas drawer behind a hamburger, which is two taps
 *  and a decision before you can reach anything. That is defensible for the fourteen
 *  screens in it and indefensible for the four that get opened every morning - so
 *  those four come out of the drawer and sit at the bottom, where a thumb already is.
 *  The hamburger stays for everything else.
 *
 *  Deliberately not five: a bar of five on a 390px screen gives each item 78px, and
 *  the labels here ("Approvals") do not fit. Four is what the width allows honestly.
 *
 *  **Add is a tab, not a screen.** It opens the same `AddTaskDrawer` the desk uses -
 *  which is already full-screen below `sm` - rather than routing to the twenty-row
 *  entry grid, which is desk work and unusable at this width.
 */
import { NavLink } from 'react-router-dom'
import { useAuth } from '@/lib/auth'

function Icon({ name }: { name: string }) {
  const d: Record<string, string> = {
    rows: 'M3 4h18v4H3zM3 11h18v4H3zM3 18h18v3H3z',
    table: 'M3 3h18v18H3zM3 9h18M9 9v12',
    stamp: 'M6 21h12M8 17h8v2H8zM9 17v-3a3 3 0 01-3-3V8a3 3 0 013-3h6a3 3 0 013 3v3a3 3 0 01-3 3v3',
    plus: 'M12 5v14M5 12h14',
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d={d[name] ?? d.rows} />
    </svg>
  )
}

const TAB = 'flex-1 flex flex-col items-center justify-center gap-0.5 h-full '
  + 'text-[10.5px] font-medium tracking-tight'

export function BottomTabs({ onAdd, approvals }: {
  /** Opens the add-a-task sheet. Passed in rather than owned here, because the sheet
   *  belongs to the page that will have to refresh its own list afterwards. */
  onAdd: () => void
  approvals?: number
}) {
  const { me, can } = useAuth()
  const mayApprove = !!me?.permissions?.approve
  const mayAdd = can('create')

  const item = (to: string, label: string, icon: string, badge?: number) => (
    <NavLink key={to} to={to}
             className={({ isActive }) =>
               `${TAB} ${isActive ? 'text-primary' : 'text-ink-3'}`}>
      <span className="relative">
        <Icon name={icon} />
        {badge ? (
          <span className="absolute -top-1 -right-2 mono text-[9.5px] leading-none
                           rounded-full bg-pend text-white px-1 py-0.5">
            {badge > 9 ? '9+' : badge}
          </span>
        ) : null}
      </span>
      {label}
    </NavLink>
  )

  return (
    /* `pb-[env(safe-area-inset-bottom)]` so the row sits above the home indicator on
       an iPhone rather than under it, where the bottom third of every tap lands on
       the system gesture instead. `h-14` plus that padding keeps each target past
       44px. */
    /* `z-[5]`, deliberately BELOW the drawers. Every sheet in this app is a
       backdrop at z-10 and a panel at z-20, so at any higher layer the bar drew on
       top of the open sheet and covered its own Save and Cancel - the add form was
       full-screen and unfinishable. Under the backdrop it is also correctly
       untappable while a sheet is open, which is what a modal means. */
    <nav className="md:hidden fixed inset-x-0 bottom-0 z-[5] bg-surface border-t
                    border-rule flex h-14 pb-[env(safe-area-inset-bottom)]"
         aria-label="Main">
      {item('/today', 'Today', 'rows')}
      {item('/tasks', 'Tasks', 'table')}
      {mayApprove && item('/approvals', 'Approvals', 'stamp', approvals)}
      {mayAdd && (
        <button type="button" onClick={onAdd}
                /* Not a NavLink: there is no /add route and inventing one would put
                   a sheet in the history stack, so Back would close it by navigating
                   rather than by closing. */
                className={`${TAB} text-ink-3`}>
          <Icon name="plus" />
          Add
        </button>
      )}
    </nav>
  )
}
